import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import type { MyFormsAuthDataResponse, MyFormsRespondentResponse } from '@core/api/my-forms/models';

@Injectable({
  providedIn: 'root',
})
export class RespondentTokenStorageService {
  private static readonly ACCESS_TOKEN = 'respondent_access_token';
  private static readonly REFRESH_TOKEN = 'respondent_refresh_token';
  private static readonly EXPIRES_IN = 'respondent_expires_in';
  private static readonly PROFILE = 'respondent_profile';

  private readonly platformId = inject(PLATFORM_ID);
  private readonly storage: Storage;

  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      this.storage = localStorage;
    } else {
      this.storage = {
        getItem: () => null,
        setItem: () => {},
        removeItem: () => {},
        clear: () => {},
        length: 0,
        key: () => null,
      };
    }
  }

  setSession(data: MyFormsAuthDataResponse): void {
    this.storage.setItem(
      RespondentTokenStorageService.ACCESS_TOKEN,
      data.accessToken,
    );
    this.storage.setItem(
      RespondentTokenStorageService.REFRESH_TOKEN,
      data.refreshToken,
    );
    this.storage.setItem(
      RespondentTokenStorageService.EXPIRES_IN,
      String(data.expiresIn),
    );
    this.storage.setItem(
      RespondentTokenStorageService.PROFILE,
      JSON.stringify(data.respondent),
    );
  }

  clearSession(): void {
    this.storage.removeItem(RespondentTokenStorageService.ACCESS_TOKEN);
    this.storage.removeItem(RespondentTokenStorageService.REFRESH_TOKEN);
    this.storage.removeItem(RespondentTokenStorageService.EXPIRES_IN);
    this.storage.removeItem(RespondentTokenStorageService.PROFILE);
  }

  isAuthenticated(): boolean {
    return !!this.getAccessToken();
  }

  getAccessToken(): string | null {
    return this.storage.getItem(RespondentTokenStorageService.ACCESS_TOKEN);
  }

  getRefreshToken(): string | null {
    return this.storage.getItem(RespondentTokenStorageService.REFRESH_TOKEN);
  }

  getProfile(): MyFormsRespondentResponse | null {
    const raw = this.storage.getItem(RespondentTokenStorageService.PROFILE);
    if (!raw) {
      return null;
    }
    try {
      return JSON.parse(raw) as MyFormsRespondentResponse;
    } catch {
      return null;
    }
  }
}
